/*
 * Copyright 2022. NXGN Management, LLC. All Rights Reserved.
 */

import { Component, Input, OnInit } from "@angular/core";
import { MerchantDetailsParentComponent } from "../merchant-details-parent.component";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService, MerchantService, NotificationService } from "../../../services";
import { AccountDetails, Merchant } from "../../../models";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { passwordMatchValidator } from "../../../validators/MPCustomValidator";

@Component({
  selector: "app-pay-pal-details",
  templateUrl: "./pay-pal-details.component.html"
})
export class PayPalDetailsComponent extends MerchantDetailsParentComponent implements OnInit {
  paypalAccountDetailsFormGroup!: FormGroup;
  editable = false;
  actionInProgress = false;
  updateMode = false;
  constructor(
    router: Router,
    activatedRoute: ActivatedRoute,
    merchantService: MerchantService,
    notificationService: NotificationService,
    authService: AuthService,
    private fb: FormBuilder
  ) {
    super(router, activatedRoute, merchantService, notificationService, authService);
  }

  @Input("merchant") merchant!: Merchant;
  faPlus = faPlus;
  ngOnInit(): void {
    this.createForm();
  }

  createForm() {
    this.paypalAccountDetailsFormGroup = this.fb.group({
      paypalPartner: [this.merchant.accountDetails.paypalAccountDetails.paypalPartner, Validators.required],
      paypalCardNotPresentUsername: [
        this.merchant.accountDetails.paypalAccountDetails.paypalCardNotPresentUsername,
        [Validators.required, Validators.maxLength(64)]
      ],
      cardNotPresentPasswordGroup: this.fb.group(
        {
          paypalCardNotPresentPassword: [
            this.merchant.accountDetails.paypalAccountDetails.paypalCardNotPresentPassword,
            [Validators.maxLength(32), Validators.minLength(6)]
          ],
          paypalCardNotPresentPasswordVerify: [
            this.merchant.accountDetails.paypalAccountDetails.paypalCardNotPresentPassword,
            [Validators.maxLength(32), Validators.minLength(6)]
          ]
        },
        { validators: passwordMatchValidator("paypalCardNotPresentPassword", "paypalCardNotPresentPasswordVerify") }
      ),
      paypalCardPresentUsername: [
        this.merchant.accountDetails.paypalAccountDetails.paypalCardPresentUsername,
        Validators.maxLength(64)
      ],
      cardPresentPasswordGroup: this.fb.group(
        {
          paypalCardPresentPassword: [
            this.merchant.accountDetails.paypalAccountDetails.paypalCardPresentPassword,
            [Validators.maxLength(32), Validators.minLength(6)]
          ],
          paypalCardPresentPasswordVerify: [
            this.merchant.accountDetails.paypalAccountDetails.paypalCardPresentPassword,
            [Validators.maxLength(32), Validators.minLength(6)]
          ]
        },
        { validators: passwordMatchValidator("paypalCardPresentPassword", "paypalCardPresentPasswordVerify") }
      )
    });

    if (!this.hasPaypalAccountDetails()) {
      this.cardNotPresentPasswordGroupControls.paypalCardNotPresentPassword.addValidators(Validators.required);
      this.cardNotPresentPasswordGroupControls.paypalCardNotPresentPasswordVerify.addValidators(Validators.required);
    }
  }

  setEditable(value: boolean, editMode: boolean) {
    this.editable = value;
    this.updateMode = editMode;
    if (!this.editable) {
      this.createForm();
    }
  }

  setPayPalAccountDetails() {
    if (this.paypalAccountDetailsFormGroup.valid) {
      this.actionInProgress = true;
      this.setFeeDetailsToNullIfNoneExist(this.merchant);
      const tempAccountDetails: AccountDetails = JSON.parse(JSON.stringify(this.merchant.accountDetails));
      this.setPaypalAccountDetails(tempAccountDetails);
      this.merchantService.updateAccountDetails(this.merchant.id, tempAccountDetails).subscribe(
        (accountDetails: AccountDetails) => {
          this.merchant.accountDetails = accountDetails;
          this.editable = false;
          this.actionInProgress = false;
        },
        (error) => {
          this.notificationService.error(error.error);
        }
      );
    } else {
      this.paypalAccountDetailsFormGroup.markAllAsTouched();
    }
  }

  hasPaypalAccountDetails(): boolean {
    return !Object.values(this.merchant.accountDetails.paypalAccountDetails).every(
      (value) => value === null || value === undefined || value === ""
    );
  }

  private setPaypalAccountDetails(accountDetails: AccountDetails) {
    accountDetails.paypalAccountDetails.paypalPartner = this.paypalAccountDetailsFormGroup.controls.paypalPartner.value;
    accountDetails.paypalAccountDetails.paypalCardNotPresentUsername =
      this.paypalAccountDetailsFormGroup.controls.paypalCardNotPresentUsername.value;
    accountDetails.paypalAccountDetails.paypalCardNotPresentPassword =
      this.cardNotPresentPasswordGroupControls.paypalCardNotPresentPassword.value;
    accountDetails.paypalAccountDetails.paypalCardPresentUsername =
      this.paypalAccountDetailsFormGroup.controls.paypalCardPresentUsername.value;
    accountDetails.paypalAccountDetails.paypalCardPresentPassword =
      this.cardPresentPasswordGroupControls.paypalCardPresentPassword.value;
  }

  get cardNotPresentPasswordGroupControls() {
    const group = this.paypalAccountDetailsFormGroup.get("cardNotPresentPasswordGroup") as FormGroup;
    return group.controls;
  }

  get cardPresentPasswordGroupControls() {
    const group = this.paypalAccountDetailsFormGroup.get("cardPresentPasswordGroup") as FormGroup;
    return group.controls;
  }
}
